
import React, { useState, useEffect } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  CalendarRange, 
  Truck, 
  Users, 
  CircleDollarSign,
  Dog,
  Check,
  FileText,
  ShieldAlert,
  AlertTriangle
} from "lucide-react";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { BookingCalendar } from "@/components/ui/booking-calendar";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCart, CartItem } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { useSiteBookings } from "@/hooks/useSiteBookings";
import { checkSiteAvailability } from "@/services/campingSite/bookingService";
import { Alert, AlertDescription } from "@/components/ui/alert";

const formSchema = z.object({
  fullName: z.string().min(2, {
    message: "Full name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  phone: z.string().min(10, {
    message: "Please enter a valid phone number.",
  }),
  address: z.string().min(2, {
    message: "Street address is required.",
  }),
  city: z.string().min(2, {
    message: "City is required.",
  }),
  state: z.string().min(2, {
    message: "State is required.",
  }),
  zipCode: z.string().min(5, {
    message: "Please enter a valid ZIP code.",
  }),
  rvType: z.string().min(1, {
    message: "Please select an RV type.",
  }),
  rvLength: z.coerce.number()
    .min(1, { message: "RV length must be at least 1 foot." })
    .max(80, { message: "RV length cannot exceed 80 feet." }),
  licensePlate: z.string().min(1, {
    message: "License plate is required.",
  }),
  guestCount: z.coerce.number()
    .min(1, { message: "Must have at least 1 guest." })
    .max(8, { message: "Maximum 8 guests allowed." }),
  hasPets: z.boolean().default(false),
  termsAgreed: z.boolean().refine(val => val === true, {
    message: "You must accept the camping regulations.",
  }),
  cancellationAgreed: z.boolean().refine(val => val === true, {
    message: "You must acknowledge the cancellation policy.",
  }),
});

type BookingFormValues = z.infer<typeof formSchema>;

interface RVBookingFormProps {
  siteId: string;
  siteName: string;
  startDate?: Date;
  endDate?: Date;
  price: number;
  totalDays?: number;
  amenities?: {
    electric?: boolean;
    amperage?: number;
    water?: boolean;
    sewage?: boolean;
    maxRVLength?: number;
    petsAllowed?: boolean;
    wifi?: boolean;
  };
  location?: string;
  onSuccess: () => void;
  editData?: CartItem | null;
}

const RVBookingForm: React.FC<RVBookingFormProps> = ({
  siteId,
  siteName,
  startDate: initialStartDate,
  endDate: initialEndDate,
  price,
  totalDays: initialTotalDays,
  amenities = {},
  location = "",
  onSuccess,
  editData
}) => {
  const { toast } = useToast();
  const { addItem, updateItem } = useCart();
  const [startDate, setStartDate] = useState<Date | undefined>(
    editData?.startDate ? new Date(editData.startDate) : initialStartDate
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    editData?.endDate ? new Date(editData.endDate) : initialEndDate
  );
  const [calculatedNights, setCalculatedNights] = useState(initialTotalDays || 0);
  const [calculatedPrice, setCalculatedPrice] = useState(price * (initialTotalDays || 1));
  const [dateConflictError, setDateConflictError] = useState<string>("");
  const navigate = useNavigate();
  
  // Get real-time booking data for this site
  const { bookings, isLoading: bookingsLoading } = useSiteBookings(siteId);
  
  // Check if we're in edit mode
  const isEditMode = !!editData;
  
  // State for dialog visibility
  const [showLegalTerms, setShowLegalTerms] = useState(false);
  const [showSafetyRequirements, setShowSafetyRequirements] = useState(false);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  
  // Check for date conflicts when dates change
  useEffect(() => {
    const checkConflicts = async () => {
      if (startDate && endDate && siteId) {
        try {
          const isAvailable = await checkSiteAvailability(siteId, startDate, endDate);
          if (!isAvailable) {
            setDateConflictError("Selected dates conflict with existing bookings. Please choose different dates.");
          } else {
            setDateConflictError("");
          }
        } catch (error) {
          console.error("Error checking availability:", error);
          setDateConflictError("Unable to verify date availability. Please try again.");
        }
      } else {
        setDateConflictError("");
      }
    };

    checkConflicts();
  }, [startDate, endDate, siteId]);
  
  useEffect(() => {
    if (startDate && endDate) {
      // Changed to exclusive calculation (nights, not including checkout day)
      const nights = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      setCalculatedNights(nights);
      setCalculatedPrice(price * nights);
    } else {
      setCalculatedNights(0);
      setCalculatedPrice(0);
    }
  }, [startDate, endDate, price]);
  
  const form = useForm<BookingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: editData?.customerName || "",
      email: editData?.customerEmail || "",
      phone: editData?.phoneNumber || "",
      address: editData?.address || "",
      city: editData?.city || "",
      state: editData?.state || "",
      zipCode: editData?.zipCode || "",
      rvType: editData?.rvType || "",
      rvLength: editData?.rvLength || 0,
      licensePlate: editData?.licensePlate || "",
      guestCount: editData?.guestCount || 1,
      hasPets: editData?.bringingPets || false,
      termsAgreed: false,
      cancellationAgreed: false,
    },
  });

  // Handler to check the acknowledgment checkbox
  const handleAcknowledgment = () => {
    form.setValue("termsAgreed", true);
  };

  function onSubmit(values: BookingFormValues) {
    if (!startDate || !endDate) {
      toast({
        title: "Date Selection Required",
        description: "Please select check-in and check-out dates.",
        variant: "destructive"
      });
      return;
    }

    if (calculatedNights < 1) {
      toast({
        title: "Minimum One Night",
        description: "Select a check-out date at least one night after check-in.",
        variant: "destructive"
      });
      return;
    }
    
    
    // Check for date conflicts before submitting
    if (dateConflictError) {
      toast({
        title: "Date Conflict",
        description: dateConflictError,
        variant: "destructive"
      });
      return;
    }
    
    const uniqueCartItemId = editData?.id || `rv-${siteId}-${Date.now()}`;
    
    console.log(`${isEditMode ? "Updating" : "Adding"} RV booking to cart:`, {
      cartItemId: uniqueCartItemId,
      siteId: siteId,
      dates: { start: startDate, end: endDate },
      customer: values.fullName
    });
    
    const itemData = {
      id: uniqueCartItemId,
      siteId: siteId,
      type: "rv-booking" as const,
      permitCategory: "camping",
      permitName: `RV Site - ${siteName}`,
      isAnnual: false,
      customerName: values.fullName,
      customerEmail: values.email,
      startDate: startDate,
      endDate: endDate,
      includesCamping: true,
      basePrice: price,
      totalPrice: calculatedPrice,
      bringingPets: values.hasPets,
      phoneNumber: values.phone,
      address: values.address,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
      licensePlate: values.licensePlate,
      campType: "rv" as const,
      rvType: values.rvType,
      rvLength: values.rvLength,
      guestCount: values.guestCount,
      itemType: "rv-site"
    };
    
    if (isEditMode) {
      // Update existing item
      updateItem(uniqueCartItemId, itemData);
      toast({
        title: "Booking Updated!",
        description: `Your booking for ${siteName} has been updated.`,
      });
    } else {
      // Add new item
      addItem(itemData as CartItem);
      toast({
        title: "Added to Cart!",
        description: `Your booking for ${siteName} has been added to cart.`,
      });
    }
    
    navigate("/cart");
  }

  // Component for legal terms dialog
  const LegalTermsDialog = () => (
    <Dialog open={showLegalTerms} onOpenChange={setShowLegalTerms}>
      <DialogContent className="max-w-md max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-tribal-primary" />
            Legal Terms
          </DialogTitle>
          <DialogDescription>
            Legal terms and conditions for camping on Shoshone-Paiute Tribal lands
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] mt-4">
          <div className="p-1">
            <div className="bg-[#FEF7CD] border border-amber-200 rounded-md p-4 mb-4">
              <div className="flex items-start gap-2">
                <span className="text-amber-600">⚠️</span>
                <div>
                  <h3 className="font-medium mb-2">Legal and Safety Acknowledgment</h3>
                  <p className="text-sm font-medium">
                    "BY EXECUTION OF A TRIBAL HUNTING, FISHING, TRAPPING, AND CAMPING PERMIT THE APPLICANT 
                    <span className="text-tribal-primary"> ACKNOWLEDGES </span> 
                    AND VOLUNTARILY CONSENTS TO CIVIL OR CRIMINAL ADJUDICATION IN THE SHOSHONE-PAIUTE 
                    TRIBAL COURT, FOR ANY INFRACTIONS OF THE CURRENT DUCK VALLEY HUNTING AND FISHING ORDINANCE, 
                    AND EXISTING FEDERAL STATUTES."
                  </p>
                </div>
              </div>
            </div>
            
            <h3 className="font-medium mb-2">Duck Valley Tribal Camping Ordinance</h3>
            <p className="text-sm mb-3">
              By purchasing a camping permit, you agree to be bound by all applicable tribal laws and regulations, including:
            </p>
            
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li>Adherence to camping regulations and quiet hours as posted at each camping site</li>
              <li>Compliance with all vehicle and RV restrictions in designated areas</li>
              <li>Proper waste disposal and environmental protection measures</li>
              <li>Recognition of tribal sovereignty and jurisdiction</li>
              <li>Submission to the authority of tribal conservation officers</li>
              <li>All permit holders must have their camping permit available for inspection at all times</li>
            </ul>
            
            <p className="mt-4 text-sm">
              Violation of these terms may result in citation, fine, confiscation of equipment, or exclusion from tribal lands. All
              legal matters arising from your activities on tribal lands will be adjudicated by the Shoshone-Paiute Tribal Court.
            </p>
          </div>
        </ScrollArea>
        <div className="flex justify-center mt-4">
          <DialogClose asChild>
            <Button 
              className="bg-tribal-primary hover:bg-tribal-primary/90"
              onClick={handleAcknowledgment}
            >
              I Understand
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Component for safety requirements dialog
  const SafetyRequirementsDialog = () => (
    <Dialog open={showSafetyRequirements} onOpenChange={setShowSafetyRequirements}>
      <DialogContent className="max-w-md max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-tribal-primary" />
            Safety Requirements
          </DialogTitle>
          <DialogDescription>
            Important safety information for camping on tribal lands
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] mt-4">
          <div className="p-1 bg-gray-50 rounded-md">
            <h3 className="font-medium mb-2">Personal Safety Requirements:</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm mb-4">
              <li>No swimming or wading in reservoir spillways or dam areas</li>
              <li>No alcohol consumption while operating watercraft</li>
              <li>Maintain safe distances from wildlife</li>
              <li>Be aware of changing weather conditions</li>
              <li>Carry sufficient water and sun protection</li>
              <li>Observe posted speed limits</li>
            </ul>
            
            <h3 className="font-medium mb-2">Environmental Safety:</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm mb-4">
              <li>Properly dispose of trash and waste</li>
              <li>Clean all equipment to prevent invasive species</li>
              <li>Observe fire restrictions during dry seasons</li>
              <li>Respect wildlife and maintain safe distances</li>
              <li>Keep vehicles on designated roads and camping areas</li>
              <li>Report any hazardous conditions to tribal authorities</li>
            </ul>
            
            <h3 className="font-medium mb-2">RV-Specific Safety:</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm mb-4">
              <li>Use proper electrical connections and avoid overloading circuits</li>
              <li>Secure all propane tanks and check for leaks regularly</li>
              <li>Keep a fire extinguisher accessible</li>
              <li>Use stabilizers when parked</li>
              <li>Respect quiet hours from 10pm to 6am</li>
            </ul>
            
            <p className="mt-4 text-sm">
              <strong>Emergency contact:</strong> For emergencies, call Tribal Police at (775) 757-3624 (Owyhee Police Department). 
              Cell service is limited in many areas, so plan accordingly.
            </p>
          </div>
        </ScrollArea>
        <div className="flex justify-center mt-4">
          <DialogClose asChild>
            <Button 
              className="bg-tribal-primary hover:bg-tribal-primary/90"
              onClick={handleAcknowledgment}
            >
              I Understand
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-tribal-primary/5 rounded-md p-4 mb-4">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <MapPin className="h-4 w-4 mr-2 text-tribal-primary" />
            Site Details
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="block text-tribal-neutral">Site Number:</span>
              <span className="font-medium">{siteName}</span>
            </div>
            <div>
              <span className="block text-tribal-neutral">Location:</span>
              <span className="font-medium">{location}</span>
            </div>
            <div>
              <span className="block text-tribal-neutral">Rate:</span>
              <span className="font-medium">${price.toFixed(2)}/night</span>
            </div>
          </div>
          
          <h4 className="text-sm font-medium mt-3 mb-1">Site Features:</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <div className="flex items-center">
              <span className={amenities.electric ? "text-green-500" : "text-red-500"}>
                {amenities.electric ? "✅" : "🚫"}
              </span>
              <span className="ml-2">
                Electric {amenities.amperage ? `${amenities.amperage}AMP` : "30AMP"}
              </span>
            </div>
            <div className="flex items-center">
              <span className={amenities.water ? "text-green-500" : "text-red-500"}>
                {amenities.water ? "✅" : "🚫"}
              </span>
              <span className="ml-2">Water hookup</span>
            </div>
            <div className="flex items-center">
              <span className={amenities.sewage ? "text-green-500" : "text-red-500"}>
                {amenities.sewage ? "✅" : "🚫"}
              </span>
              <span className="ml-2">Sewage hookup</span>
            </div>
            <div className="flex items-center">
              <span className={amenities.wifi ? "text-green-500" : "text-red-500"}>
                {amenities.wifi ? "✅" : "🚫"}
              </span>
              <span className="ml-2">WiFi</span>
            </div>
            {amenities.maxRVLength && (
              <div className="flex items-center col-span-2">
                <span className="text-tribal-primary">ℹ️</span>
                <span className="ml-2">Max RV Length: {amenities.maxRVLength}ft</span>
              </div>
            )}
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CalendarRange className="h-4 w-4 mr-2 text-tribal-primary" />
            Select Dates
          </h3>
          
          {/* Date Conflict Warning */}
          {dateConflictError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {dateConflictError}
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <BookingCalendar
              date={startDate}
              setDate={setStartDate}
              label="Check-in Date"
              placeholder="Select check-in date"
              bookings={bookings}
              disabled={bookingsLoading}
              mode="checkin"
            />
            <BookingCalendar
              date={endDate}
              setDate={setEndDate}
              label="Check-out Date"
              placeholder="Select check-out date"
              bookings={bookings}
              disabled={bookingsLoading || !startDate}
               mode="checkout"
               defaultMonth={startDate}
                highlightDate={startDate}
                rangeEnd={endDate}
            />
          </div>

          {startDate && endDate && calculatedNights < 1 && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Select a check-out date at least one night after check-in.
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <Separator />
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <User className="h-4 w-4 mr-2 text-tribal-primary" />
            Customer Information
          </h3>
          
          <div className="grid grid-cols-1 gap-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name <span className="text-destructive ml-1">*</span></FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email <span className="text-destructive ml-1">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number <span className="text-destructive ml-1">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <MapPin className="h-4 w-4 mr-2 text-tribal-primary" />
            Address Information
          </h3>
          
          <div className="grid grid-cols-1 gap-4">
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address <span className="text-destructive ml-1">*</span></FormLabel>
                  <FormControl>
                    <Input placeholder="Enter street address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City <span className="text-destructive ml-1">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="City" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State <span className="text-destructive ml-1">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="State" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem className="col-span-2 sm:col-span-1">
                    <FormLabel>ZIP Code <span className="text-destructive ml-1">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="12345" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <Truck className="h-4 w-4 mr-2 text-tribal-primary" />
            RV & Vehicle Information
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="rvType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RV Type <span className="text-destructive ml-1">*</span></FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select RV Type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="fifth-wheel">Fifth Wheel</SelectItem>
                      <SelectItem value="motorhome-a">Motorhome Class A</SelectItem>
                      <SelectItem value="motorhome-b">Motorhome Class B</SelectItem>
                      <SelectItem value="motorhome-c">Motorhome Class C</SelectItem>
                      <SelectItem value="popup">Popup</SelectItem>
                      <SelectItem value="travel-trailer">Travel Trailer</SelectItem>
                      <SelectItem value="truck-camper">Truck Camper</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="rvLength"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RV Length (ft) <span className="text-destructive ml-1">*</span></FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      max={amenities.maxRVLength || 80} 
                      placeholder="Length in feet" 
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    {amenities.maxRVLength 
                      ? `Maximum length: ${amenities.maxRVLength}ft` 
                      : "Maximum length: 40ft"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="licensePlate"
              render={({ field }) => (
                <FormItem className="col-span-full">
                  <FormLabel>Vehicle License Plate(s) <span className="text-destructive ml-1">*</span></FormLabel>
                  <FormControl>
                    <Input placeholder="ABC123" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <Users className="h-4 w-4 mr-2 text-tribal-primary" />
            Guests & Pets
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="guestCount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Number of Guests <span className="text-destructive ml-1">*</span></FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      max="8" 
                      placeholder="Number of guests" 
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Maximum 8 guests per site
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="hasPets"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between space-x-2 rounded-md border p-3 mt-7">
                  <div className="space-y-0.5">
                    <FormLabel className="flex items-center">
                      <Dog className="h-4 w-4 mr-2" />
                      Bringing Pets?
                    </FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <div className="bg-tribal-primary/5 p-4 rounded-md">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CircleDollarSign className="h-4 w-4 mr-2 text-tribal-primary" />
            Pricing Summary
          </h3>
          
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Rate per night:</span>
              <span>${price.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Total nights:</span>
              <span>{calculatedNights}</span>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between font-medium">
              <span>Total cost:</span>
              <span>${calculatedPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        {/* New Acknowledgement Section */}
        <div className="bg-white border rounded-md p-4">
          <FormField
            control={form.control}
            name="termsAgreed"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm">
                    I acknowledge and agree to the{" "}
                    <Button 
                      variant="link" 
                      className="h-auto p-0 text-tribal-primary underline"
                      type="button"
                      onClick={() => setShowLegalTerms(true)}
                    >
                      legal terms
                    </Button>
                    {" "}and{" "}
                    <Button 
                      variant="link" 
                      className="h-auto p-0 text-tribal-primary underline"
                      type="button"
                      onClick={() => setShowSafetyRequirements(true)}
                    >
                      safety
                    </Button>
                    {" "}requirements for camping on tribal lands
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        
          <FormField
            control={form.control}
            name="cancellationAgreed"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm">
                    I acknowledge and agree to the{" "}
                    <Button 
                      variant="link" 
                      className="h-auto p-0 text-tribal-primary underline"
                      type="button"
                      onClick={() => setShowCancellationPolicy(true)}
                    >
                      cancellation policy
                    </Button>
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button 
            type="submit" 
            className="flex-1 bg-tribal-primary hover:bg-tribal-primary/90"
            disabled={!!dateConflictError || !startDate || !endDate || calculatedNights < 1}
          >
            {isEditMode ? "Update Booking" : "Add to Cart"}
          </Button>
          <Button 
            type="button" 
            variant="outline"
            className="flex-1"
            onClick={onSuccess}
          >
            Cancel
          </Button>
        </div>
      </form>
      
      {/* Legal Terms Dialog */}
      <LegalTermsDialog />
      
      {/* Safety Requirements Dialog */}
      <SafetyRequirementsDialog />
      
      {/* Cancellation Policy Dialog */}
      <Dialog open={showCancellationPolicy} onOpenChange={setShowCancellationPolicy}>
        <DialogContent className="max-w-md max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-tribal-primary" />
              Cancellation & Refund Policy
            </DialogTitle>
            <DialogDescription>
              Please review the cancellation and refund policy for RV campsite reservations
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] mt-4">
            <div className="p-1 space-y-4">
              <div>
                <h3 className="font-medium mb-2">RV Campsite Reservations</h3>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>To cancel a reservation, customers must call or email at least <strong>five (5) business days</strong> prior to the scheduled arrival date.</li>
                  <li>Cancellations made within five (5) business days of arrival are <strong>non-refundable</strong>.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Emergency Exceptions</h3>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>In the event of an emergency or death, cancellation requests may be considered on a case-by-case basis.</li>
                  <li>Any exception must receive approval from the Director.</li>
                  <li>Approved exceptions may result in a partial or full refund at the Tribe's discretion.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Contact Requirement</h3>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>All cancellations must be made by phone or email through the appropriate Tribal office.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Processing Fees</h3>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li>All payment processing fees are <strong>non-refundable</strong>.</li>
                </ul>
              </div>
              
              <div className="bg-muted/50 border rounded-md p-3">
                <p className="text-sm">
                  <strong>Acknowledgment:</strong> By completing your reservation, you acknowledge and agree to this Cancellation & Refund Policy.
                </p>
              </div>
            </div>
          </ScrollArea>
          <div className="flex justify-center mt-4">
            <DialogClose asChild>
              <Button className="bg-tribal-primary hover:bg-tribal-primary/90">
                I Understand
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default RVBookingForm;
